import React, { useState, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";

import {
  ItemType,
  Order,
  Product,
  UPDATE_ORDER,
  updateProduct,
  GET_ORDER_BY_UNIQUE_ATTRIBUTE,
  ImprintChoiceRef,
  OrderTypeChoiceRef,
  MaterialRef,
  AestheticsRef,
  Rdv2ChoiceRef,
  TryInFeedbackRef,
  PartialDentureOrderTypeChoiceRef,
  PartialDentureMaterialRef,
  PartialDentureAestheticsRef,
  PartialDentureRdv2ChoiceRef,
  PartialDentureTryInFeedbackRef,
  Rdv2PreviousProducts,
  Stepper,
  FullDentureRefs,
  mapFieldsToValues,
  stepLabels,
  useWorkflow,
  useWorkflowFieldsAndDefaultValues,
  PartialDentureRefs,
  useCreateProduct,
  HooksAndOptionsMappedValues,
  InformationMappedValues,
  ProsthesisMappedValues,
  ItemMaterials,
  RemovableItemOrderType,
} from "@jasper/shared";

import { notifications } from "@mantine/notifications";
import { Loader } from "@mantine/core";
import { LoadingOverlay } from "@mantine/core";
import { Box } from "@mantine/core";
import { useFeatureFlagEnabled } from "posthog-js/react";

const isProductRdv2 = (order: Order, product: Product): boolean => {
  const isFullDenture = product?.productType === ItemType.FULL_DENTURE;
  const isPartialDenture = product?.productType === ItemType.PARTIAL_DENTURE;

  if (!isFullDenture && !isPartialDenture) return false;

  const removableItem = product?.removableItem?.[0];

  return (removableItem?.rdvNumber ?? 0) >= 2;
};

export const SteppedOrderEdit = () => {
  const { order_id, product_id } = useParams();
  const location = useLocation();
  const isRdv2FromNavigation =
    (location.state as { isRdv2Continuation?: boolean })?.isRdv2Continuation ??
    false;
  if (!order_id || !product_id) {
    console.error("No order_id or product_id found");
    return (
      <Box p="lg">
        <span style={{ color: "red" }}>
          Invalid order or product ID. Please check the URL.
        </span>
      </Box>
    );
  }
  const navigate = useNavigate();
  const { data, loading, refetch } = useQuery(GET_ORDER_BY_UNIQUE_ATTRIBUTE, {
    variables: {
      where: {
        id: order_id,
      },
    },
  });

  const [updateOrder, { loading: updateOrderLoading }] =
    useMutation(UPDATE_ORDER);
  const useNewPapWorkflow =
    useFeatureFlagEnabled("new_pap_workflow_v2") ?? false;
  const partialDentureOrderTypeChoiceRef =
    useRef<PartialDentureOrderTypeChoiceRef>(null);
  const partialDentureMaterialRef = useRef<PartialDentureMaterialRef>(null);
  const partialDentureAestheticsRef = useRef<PartialDentureAestheticsRef>(null);
  const partialDentureRdv2ChoiceRef = useRef<PartialDentureRdv2ChoiceRef>(null);
  const partialDentureTryInFeedbackRef =
    useRef<PartialDentureTryInFeedbackRef>(null);

  const partialDentureRefs: PartialDentureRefs = {
    orderTypeChoiceRef: partialDentureOrderTypeChoiceRef,
    materialRef: partialDentureMaterialRef,
    aestheticsRef: partialDentureAestheticsRef,
    rdv2ChoiceRef: partialDentureRdv2ChoiceRef,
    tryInFeedbackRef: partialDentureTryInFeedbackRef,
  };

  const order = data?.getOrderByUniqueAttribute;
  const product = order?.products?.find(
    (product: Product) => product.id === product_id
  );

  const partialDentureOrderType =
    (product?.removableItem?.[0]
      ?.partialDentureOrderType as RemovableItemOrderType) ?? null;

  const isRdv2Product = isRdv2FromNavigation || isProductRdv2(order, product);

  const productIndex = order?.products?.findIndex(
    (product: Product) => product.id === product_id
  );
  const fullDentureOrderType = useMemo(() => {
    const removableItem = product?.removableItem?.[0];
    return (
      (removableItem?.fullDentureOrderType as RemovableItemOrderType) ?? null
    );
  }, [
    product?.removableItem?.[0]?.id,
    product?.removableItem?.[0]?.fullDentureOrderType,
  ]);

  const previousProduct = useMemo(() => {
    if (!isRdv2Product || !order?.products) return undefined;

    const isFullDenture = product?.productType === ItemType.FULL_DENTURE;
    // Find the last FULL_DENTURE product that is not the current one
    // Prefer WAX_BITE or TRY_IN
    if (isFullDenture) {
      const waxBiteOrTryIn = order.products.findLast(
        (p: Product) =>
          p.id !== product_id &&
          p.productType === ItemType.FULL_DENTURE &&
          (p.removableItem?.[0]?.fullDentureOrderType ===
            RemovableItemOrderType.WAX_BITE ||
            p.removableItem?.[0]?.fullDentureOrderType ===
              RemovableItemOrderType.TRY_IN)
      );
      if (waxBiteOrTryIn) return waxBiteOrTryIn;
      // fallback to the last FULL_DENTURE product
      return order.products.findLast(
        (p: Product) =>
          p.id !== product_id && p.productType === ItemType.FULL_DENTURE
      );
    }

    // PARTIAL_DENTURE
    const waxBiteOrTryIn = order.products.findLast(
      (p: Product) =>
        p.id !== product_id &&
        p.productType === ItemType.PARTIAL_DENTURE &&
        (p.removableItem?.[0]?.partialDentureOrderType ===
          RemovableItemOrderType.WAX_BITE ||
          p.removableItem?.[0]?.partialDentureOrderType ===
            RemovableItemOrderType.TRY_IN)
    );
    if (waxBiteOrTryIn) return waxBiteOrTryIn;
    return order.products.findLast(
      (p: Product) =>
        p.id !== product_id && p.productType === ItemType.PARTIAL_DENTURE
    );
  }, [isRdv2Product, order?.products, product_id, product?.productType]);

  const previousProducts = useMemo<Rdv2PreviousProducts | undefined>(
    () =>
      product?.productType && previousProduct
        ? { [product.productType]: previousProduct }
        : undefined,
    [product?.productType, previousProduct]
  );

  if (!product) {
    console.error("No product found");
  }
  const [page, setPage] = useState(() => {
    try {
      return parseInt(
        localStorage.getItem(`orderUpdatePage-${order_id}-${product_id}`) ??
          "0",
        10
      );
    } catch {
      return 0;
    }
  });

  const workflowWeCanHandle =
    (product?.productType as ItemType) === ItemType.PARTIAL_DENTURE
      ? [ItemType.PARTIAL_DENTURE]
      : (product?.productType as ItemType) === ItemType.FULL_DENTURE
        ? [ItemType.FULL_DENTURE]
        : [];

  const imprintChoiceRef = useRef<ImprintChoiceRef>(null);
  const orderTypeChoiceRef = useRef<OrderTypeChoiceRef>(null);
  const materialRef = useRef<MaterialRef>(null);
  const aestheticsRef = useRef<AestheticsRef>(null);
  const rdv2ChoiceRef = useRef<Rdv2ChoiceRef>(null);
  const tryInFeedbackRef = useRef<TryInFeedbackRef>(null);

  const fullDentureRefs: FullDentureRefs = {
    imprintChoiceRef,
    orderTypeChoiceRef,
    materialRef,
    aestheticsRef,
    rdv2ChoiceRef,
    tryInFeedbackRef,
  };

  const { removableItemChoicesData, removableItemChoicesLoading } =
    useCreateProduct(workflowWeCanHandle);

  const workflow = useWorkflowFieldsAndDefaultValues({
    workflow: workflowWeCanHandle,
    order: (data?.getOrderByUniqueAttribute as unknown as Order) ?? undefined,
    product: (product as unknown as Product) ?? {},
    isLabWorkflow: true,
    fullDentureOrderType,
    isRdv2Continuation: isRdv2Product,
    previousProducts,
    useNewPapWorkflow,
    partialDentureRefs,
    partialDentureOrderType,
  });

  const methods = useForm({
    defaultValues: workflow.workflowDefaultValues,
  });

  const updateOrderWrapper = async (data: {
    variables: Record<string, string>;
  }) => {
    const result = await updateOrder(data);
    return result.data?.updateOrder as Order;
  };
  const save = async (
    mappedValues:
      | InformationMappedValues
      | ProsthesisMappedValues
      | HooksAndOptionsMappedValues
  ) => {
    const currentStepLabel = workflowToDisplay?.[0]?.[page]?.label ?? "";
    try {
      const result = await updateProduct(
        updateOrderWrapper,
        mappedValues,
        order_id as string,
        product.productType,
        currentStepLabel,
        product
      );
      if (result) {
        refetch();
        return true;
      }
      return false;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const getCurrentPageFormFieldsValues = () => {
    if (!workflow) return { fields: [], values: {}, mappedValues: {} };
    const stepFields = workflow.workflowFields[0];
    const currentField = stepFields[page];
    const fields = Object.keys(currentField);
    const values = methods.getValues(fields);
    const mappedValues = mapFieldsToValues(fields, values);
    return { fields, values, mappedValues };
  };

  const nextStep = async () => {
    const isFullDenture = product?.productType === ItemType.FULL_DENTURE;
    const isLastStep = page === (workflowToDisplay?.[0]?.length ?? 1) - 1;

    const isPartialDenture = product?.productType === ItemType.PARTIAL_DENTURE;

    if (isPartialDenture && useNewPapWorkflow) {
      const stepLabel = workflowToDisplay?.[0]?.[page]?.label;

      try {
        if (stepLabel === stepLabels.PARTIAL_DENTURE.ORDER_TYPE) {
          await partialDentureRefs.orderTypeChoiceRef.current?.saveData();
        } else if (stepLabel === stepLabels.PARTIAL_DENTURE.MATERIAL) {
          await partialDentureRefs.materialRef.current?.saveData();
        } else if (stepLabel === stepLabels.PARTIAL_DENTURE.AESTHETICS) {
          await partialDentureRefs.aestheticsRef.current?.saveData();
        } else if (stepLabel === stepLabels.PARTIAL_DENTURE.CHOICE) {
          await partialDentureRefs.rdv2ChoiceRef?.current?.saveData();
        } else if (stepLabel === stepLabels.PARTIAL_DENTURE.TRY_IN_FEEDBACK) {
          await partialDentureRefs.tryInFeedbackRef?.current?.saveData();
        } else if (
          stepLabel === stepLabels.PARTIAL_DENTURE.INFORMATION ||
          stepLabel === stepLabels.PARTIAL_DENTURE.HOOKS_AND_OPTIONS
        ) {
          const { fields, mappedValues } = getCurrentPageFormFieldsValues();
          const isValid = await methods.trigger(fields);
          if (!isValid) return;

          await save(
            mappedValues as
              InformationMappedValues | HooksAndOptionsMappedValues
          );
        }
        await refetch();
        if (isLastStep) {
          navigate(`/orders/${order_id}`);
        } else {
          setPage(page + 1);
        }
      } catch (e) {
        if (!(e instanceof Error) || e.message !== "validation_error") {
          console.error(e);
          notifications.show({
            title: "Erreur",
            message: "Une erreur est survenue lors de la sauvegarde.",
            color: "red",
          });
        }
      }
    } else if (isFullDenture) {
      const stepLabel = workflowToDisplay?.[0]?.[page]?.label;

      try {
        if (stepLabel === stepLabels.FULL_DENTURE.IMPRINT_CHOICE) {
          await fullDentureRefs.imprintChoiceRef.current?.saveData();
        } else if (stepLabel === stepLabels.FULL_DENTURE.ORDER_TYPE) {
          await fullDentureRefs.orderTypeChoiceRef.current?.saveData();
        } else if (stepLabel === stepLabels.FULL_DENTURE.MATERIAL) {
          await fullDentureRefs.materialRef.current?.saveData();
        } else if (stepLabel === stepLabels.FULL_DENTURE.AESTHETICS) {
          await fullDentureRefs.aestheticsRef.current?.saveData();
        } else if (stepLabel === stepLabels.FULL_DENTURE.CHOICE) {
          await fullDentureRefs.rdv2ChoiceRef.current?.saveData();
        } else if (stepLabel === stepLabels.FULL_DENTURE.TRY_IN_FEEDBACK) {
          await fullDentureRefs.tryInFeedbackRef.current?.saveData();
        }

        await refetch();

        if (isLastStep) {
          navigate(`/orders/${order_id}`);
        } else {
          setPage(page + 1);
        }
      } catch (e) {
        console.error(e);
        notifications.show({
          title: "Erreur",
          message: "Une erreur est survenue lors de la sauvegarde.",
          color: "red",
        });
      }
    } else {
      const { fields, mappedValues } = getCurrentPageFormFieldsValues();
      const isValid = await methods.trigger(fields);
      if (isValid) {
        try {
          const result = await save(
            mappedValues as
              | InformationMappedValues
              | ProsthesisMappedValues
              | HooksAndOptionsMappedValues
          );
          if (result && page < workflow.workflowFields[0].length - 1) {
            setPage(page + 1);
          } else if (page === workflow.workflowFields[0].length - 1) {
            navigate(`/orders/${order_id}`);
          } else {
            notifications.show({
              title: "Erreur",
              message: "Une erreur est survenue lors de la sauvegarde.",
              color: "red",
            });
          }
        } catch (e) {
          console.error(e);
          notifications.show({
            title: "Erreur",
            message: "Une erreur est survenue lors de la sauvegarde.",
            color: "red",
          });
        }
      }
    }
  };

  const prevStep = () => {
    const prevStepValue = workflow.workflowMap?.[productIndex]?.[page - 1];
    const values = order?.products
      ?.map((product: Product) =>
        product.removableItem?.map(item => item.itemMaterial?.id)
      )
      .flat();

    if (
      prevStepValue?.label === stepLabels.PARTIAL_DENTURE.HOOKS_AND_OPTIONS &&
      (values?.length ?? 0) > 0 &&
      values?.every((value: ItemMaterials) => value === ItemMaterials.VALPLAST)
    ) {
      setPage(page - 2);
    } else {
      setPage(page - 1);
    }
  };
  const workflowToDisplay =
    order && order !== undefined
      ? useWorkflow(
          false,
          workflowWeCanHandle,
          true,
          order as Order,
          undefined,
          removableItemChoicesData,
          nextStep,
          prevStep,
          product,
          undefined,
          refetch,
          undefined,
          undefined,
          undefined,
          undefined,
          fullDentureRefs,
          fullDentureOrderType,
          isRdv2Product,
          previousProducts,
          useNewPapWorkflow,
          partialDentureRefs,
          partialDentureOrderType
        )
      : null;

  const isLoading =
    loading || updateOrderLoading || removableItemChoicesLoading;
  if (!loading && !data) {
    console.error("No data found");
    return (
      <Box p="lg">
        <span style={{ color: "red" }}>
          Order data not found. Please try again.
        </span>
      </Box>
    );
  }

  return (
    <Box style={{ height: "calc(100vh - 60px)" }}>
      <LoadingOverlay visible={isLoading}>
        <Loader />
      </LoadingOverlay>
      {!isLoading && workflowToDisplay && workflowToDisplay[0] && (
        <Box p="lg">
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(nextStep)}
              style={{ height: "100%" }}
            >
              <Stepper
                variant="textDown"
                data={workflowToDisplay[0]}
                page={page}
                setPage={setPage}
                productIndex={0}
                workflowStepperUnfiltered={workflowToDisplay}
                setProductIndex={() => {}}
                onNext={nextStep}
                onPrev={prevStep}
              />
            </form>
          </FormProvider>
        </Box>
      )}
    </Box>
  );
};
